import { OrganizationSwitcher } from '@develio/auth/client';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { type FeatureKey, useFeatures } from '../lib/features';
import { useIsAdmin } from '../lib/useIsAdmin';

/**
 * Left nav sidebar.
 *
 * Sprint 21: adds Clerk OrganizationSwitcher for multi-org consultant workflow.
 * Post-v0.3.0: sections are collapsible. Default state is "Analysis expanded,
 * everything else collapsed," but whichever section contains the current route
 * auto-expands, and user-toggled state persists in localStorage.
 */
/**
 * Nav sections. Any section or item tagged with `feature` is hidden when
 * that platform flag is disabled. Kill-switching here is purely cosmetic —
 * the API also 404s the corresponding routes (see feature-gate plugin).
 */
type NavItem = { to: string; label: string; feature?: FeatureKey };
type NavSection = {
  key: string;
  label: string;
  /** If true, starts expanded on first visit. */
  defaultOpen?: boolean;
  /** If set, whole section is hidden when the flag is off. */
  feature?: FeatureKey;
  /** If true, section is only visible to users in ADMIN_USER_IDS. */
  adminOnly?: boolean;
  items: ReadonlyArray<NavItem>;
};

const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  {
    key: 'analysis',
    label: 'Analysis',
    defaultOpen: true,
    items: [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/analyses', label: 'Analyses' },
      { to: '/analyses/new', label: '+ New Analysis' },
    ],
  },
  // Inputs that change analysis results — promoted out of Settings so the
  // owner can find them without spelunking. Kept above intake/market sections
  // because they're the things a trial user touches after their first run.
  {
    key: 'organization',
    label: 'Organization',
    defaultOpen: true,
    items: [
      { to: '/onboarding', label: 'Quick Setup' },
      { to: '/settings/jurisdiction', label: 'Jurisdiction' },
      { to: '/settings/methodology', label: 'Methodology' },
      { to: '/settings/scoring', label: 'Scoring Rubric' },
      { to: '/settings/rims', label: 'RIMS Multipliers' },
    ],
  },
  {
    key: 'intake',
    label: 'Intake Forms',
    feature: 'intake_forms',
    items: [
      { to: '/forms', label: 'All Forms' },
      { to: '/forms/new', label: '+ New Form' },
    ],
  },
  {
    key: 'market',
    label: 'Market Intelligence',
    items: [
      { to: '/wage-benchmarks', label: 'Wage Benchmarks' },
      { to: '/workforce-profile', label: 'Workforce Profile' },
      { to: '/industry-targets', label: 'Industry Targets' },
      { to: '/community-health', label: 'Community Health' },
    ],
  },
  // Everything not directly tied to running an analysis — billing, members,
  // governance, audit, integrations. Collapsed by default so the sidebar
  // reads as a workflow, not a config tree.
  {
    key: 'settings',
    label: 'Settings',
    items: [
      { to: '/settings/organization', label: 'Organization Details' },
      { to: '/settings/members', label: 'Members' },
      { to: '/settings/billing', label: 'Billing' },
      { to: '/settings/governance', label: 'Governance' },
      { to: '/settings/audit', label: 'Audit Log' },
      { to: '/settings/api-keys', label: 'API Keys', feature: 'api_keys' },
      { to: '/settings/webhooks', label: 'Webhooks', feature: 'webhooks' },
      { to: '/settings/sso', label: 'SSO', feature: 'sso' },
    ],
  },
  {
    key: 'help',
    label: 'Help',
    items: [
      { to: '/help', label: 'Getting Started Guide' },
      { to: '/help/methodology', label: 'Methodology & Calculations' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    adminOnly: true,
    items: [
      { to: '/admin', label: 'Organizations' },
      { to: '/admin/features', label: 'Feature Flags' },
      { to: '/admin/waitlist', label: 'Waitlist' },
    ],
  },
];

const STORAGE_KEY = 'develio:sidebar:openSections';

/** Read persisted open-section keys from localStorage. Returns null if unset/invalid. */
function readPersistedOpenSections(): Set<string> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((k): k is string => typeof k === 'string'));
  } catch {
    return null;
  }
}

function persistOpenSections(open: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...open]));
  } catch {
    // Storage full or disabled — non-fatal.
  }
}

/** Does this section contain the current pathname (as a child-route prefix)? */
function sectionContainsPath(section: (typeof NAV_SECTIONS)[number], pathname: string): boolean {
  return section.items.some((item) => {
    // Exact match or child route (e.g. /analyses/:id matches /analyses).
    if (pathname === item.to) return true;
    // Only treat as prefix when the item path is not itself a "new" leaf that
    // would otherwise over-match (e.g. /analyses/new should not claim /analyses/:id).
    // We use the longest matching item, resolved at the section level via .some().
    return pathname.startsWith(`${item.to}/`);
  });
}

export function Sidebar() {
  const location = useLocation();
  const { features } = useFeatures();
  const isAdmin = useIsAdmin();

  // Filter nav tree by feature flags + admin gate. Recomputed only when
  // inputs change so reference identity is stable across renders.
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.filter(
        (s) => (!s.feature || features[s.feature]) && (!s.adminOnly || isAdmin),
      ).map((s) => ({
        ...s,
        items: s.items.filter((item) => !item.feature || features[item.feature]),
      })),
    [features, isAdmin],
  );

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const persisted = readPersistedOpenSections();
    if (persisted) return persisted;
    return new Set(NAV_SECTIONS.filter((s) => s.defaultOpen).map((s) => s.key));
  });

  // Auto-expand the section containing the current route whenever the path
  // changes. We never auto-collapse — user collapses are sticky.
  useEffect(() => {
    const activeSection = visibleSections.find((s) => sectionContainsPath(s, location.pathname));
    if (!activeSection) return;
    setOpenSections((prev) => {
      if (prev.has(activeSection.key)) return prev;
      const next = new Set(prev);
      next.add(activeSection.key);
      return next;
    });
  }, [location.pathname, visibleSections]);

  // Persist whenever the set changes.
  useEffect(() => {
    persistOpenSections(openSections);
  }, [openSections]);

  const toggleSection = (key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-4">
        <div className="mb-3">
          <img src="/logo.png" alt="develio" className="h-14 w-auto" />
        </div>
        {/* Multi-org switcher — lets consultants switch between EDC clients */}
        <OrganizationSwitcher
          hidePersonal
          afterSelectOrganizationUrl="/dashboard"
          appearance={{
            elements: {
              rootBox: 'w-full',
              organizationSwitcherTrigger:
                'w-full justify-start px-2 py-1.5 text-sm rounded-md hover:bg-slate-100',
            },
          }}
        />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {visibleSections.map((section) => {
          const isOpen = openSections.has(section.key);
          const hasActiveChild = sectionContainsPath(section, location.pathname);
          const panelId = `sidebar-section-${section.key}`;
          return (
            <div key={section.key} className="mb-4">
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                data-tour={
                  section.label === 'Market Intelligence' ? 'market-intelligence' : undefined
                }
                className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-develio-green transition-colors hover:bg-slate-50"
              >
                <span className="flex items-center gap-2">
                  {section.label}
                  {!isOpen && hasActiveChild && (
                    <span
                      aria-hidden="true"
                      className="inline-block h-1.5 w-1.5 rounded-full bg-develio-teal"
                      title="Active page is in this section"
                    />
                  )}
                </span>
                <svg
                  aria-hidden="true"
                  className={`h-3 w-3 text-slate-400 transition-transform ${
                    isOpen ? 'rotate-90' : ''
                  }`}
                  viewBox="0 0 12 12"
                  fill="currentColor"
                >
                  <path d="M4 2l4 4-4 4V2z" />
                </svg>
              </button>
              {isOpen && (
                <ul id={panelId} className="mt-1 space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/analyses'}
                        data-tour={item.to === '/settings/billing' ? 'billing' : undefined}
                        className={({ isActive }) =>
                          `block rounded-md px-3 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-develio-teal/10 font-semibold text-develio-tealDark'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
